import { uniq } from 'lodash';

import { getProviderDisplayNameFromIntegrationClientKind } from '@/modules/assetProviderIntegrations/assetProviderIntegrations.utils';
import { getHouseholdIntegration } from '@/modules/household/householdIntegrations';
import { HouseholdBillableKind, HouseholdWhereInput } from '@/types/schema';

import { ClientSearchFilterType } from './ClientSearchFilterToolbar/ClientSearchFilterToolbar.types';
import { ClientListAdvisorClientFragment } from './graphql/GetClients.generated';

export type ClientRowData = ReturnType<typeof mapDataToRows>[number];
export interface ClientTable extends ClientRowData {
  icon: unknown;
}

// A household can have multiple primary clients (grantors) in different states;
// show each distinct one, preserving possiblePrimaryClients order.
function getHouseholdState(
  household: ClientListAdvisorClientFragment
): string | null {
  const stateCodes = uniq(
    (household.possiblePrimaryClients ?? [])
      .map((client) => client.address?.stateCode?.toUpperCase())
      .filter((stateCode): stateCode is string => Boolean(stateCode))
  );

  return stateCodes.length > 0 ? stateCodes.join(', ') : null;
}

export function mapDataToRows(households: ClientListAdvisorClientFragment[]) {
  return (
    households?.flatMap((household) => {
      if (!household) return [];
      const integrationProvider = getHouseholdIntegration(household);

      return {
        id: household.id,
        displayName: household.displayName,
        sortable_last_name: household.displayName,
        totalAssets: household.estateValueMetrics.totalEstateValue,
        state: getHouseholdState(household),
        primary_relationship_owner_name:
          household.primaryRelationshipOwner?.displayName ?? null,
        created_at: household.createdAt,
        sub_brand_display_name: household.subBrand?.displayName,
        teams: household.assignedTeams?.map((team) => team.name) ?? [],
        isProspect: household.billableKind === HouseholdBillableKind.Prospect,
        isArchived: household.archivedAt != null,
        integrationProvider: integrationProvider
          ? getProviderDisplayNameFromIntegrationClientKind(integrationProvider)
          : null,
        notes: household.notes ?? null,
        private: household.private,
        hasPendingValidationQuestions: household.hasPendingValidationQuestions,
      };
    }) ?? []
  );
}

export function getFilterVariables(
  filterValue: Record<ClientSearchFilterType, string[]>
): HouseholdWhereInput {
  const where: HouseholdWhereInput = {};

  if (filterValue.subBrand.length > 0) {
    where.hasSubBrandWith = [
      {
        or: filterValue.subBrand.map((id) => ({ id })),
      },
    ];
  }

  if (filterValue.relationshipOwner.length > 0) {
    where.hasPrimaryRelationshipOwnerWith = [
      {
        or: filterValue.relationshipOwner.map((id) => ({ id })),
      },
    ];
  }

  if (filterValue.state?.length > 0) {
    // Scope to isPrimary so the filter matches the State column (grantors only).
    where.hasClientProfilesWith = [
      {
        isPrimary: true,
        or: filterValue.state.map((stateCode) => ({
          hasAddressWith: [{ stateCode }],
        })),
      },
    ];
  }

  if (filterValue.team?.length > 0) {
    where.hasAssignedTeamsWith = [
      {
        or: filterValue.team.map((id) => ({ id })),
      },
    ];
  }

  return where;
}
